// Global Instructions Rule Applied!

import { isListFieldValue, isScalarFieldValue, type FieldValue } from '../types.ts'
import React from 'react'
import { Tag } from 'antd'
import { ContactBadges } from '../../ContactBadges.jsx'
import { CompanyBadges } from '../../CompanyBadges.jsx'
import {
  FieldConfig,
  FieldTypeEnum,
  SelectTypeEnum,
  NumberFieldConfig,
  ValuationFieldConfig,
  ContactFieldConfig
} from '../types.ts'
import {
  formatNumberNoDecimal,
  formatCurrency,
  getSelectDisplayValue,
  getCompanyDisplayValue,
  getCountryDisplayValue,
  getCityDisplayValue,
  formatDateDisplay,
  defaultFormatters
} from './formatters.ts'
import {
  isDateFieldConfig,
  isValuationFieldConfig,
  isContactFieldConfig,
  isSelectFieldConfig,
  isCompanyFieldConfig,
  isTagsFieldConfig
} from './typeGuards.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'
import { CopyButton } from '../../CopyButton.tsx'
import { externalHref, mailHref, telHref } from '../../../utils/contactHref.ts'

// Global ref to track pending hyperlink clicks (shared across all instances)
const pendingHyperlinkClicksRef = { current: new Map<string, number>() }

export interface DisplayValueOptions {
  textClassName?: string
  getLookupOptions?: (category: string) => SelectListEntry[]
  labelPosition?: 'above' | 'inline' | 'none'
  /** Handler for opening forms when hyperlinks are clicked */
  onHyperlinkClick?: (module: string, recordId: string, title: string) => void
  /** Countries data for resolving country codes to names */
  countriesData?: Array<{ code: string; name: string }>
  /** Whether the field is readonly (should not show cursor icons) */
  readonly?: boolean
  /** Alignment for wrapped content: 'left' | 'right' | 'center' */
  alignment?: 'left' | 'right' | 'center'
}

/** The number / currency displays format scalars; any other value shape is shown as its string form, as before. */
function scalarForDisplay(value: FieldValue): string | number | null | undefined {
  return isScalarFieldValue(value) ? value : String(value)
}

function formatNumberWithMonospace(
  value: string | number | null | undefined,
  textClassName?: string,
  labelPosition?: 'above' | 'inline' | 'none'
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return labelPosition && labelPosition !== 'none' ? '-' : ''
  }

  const numValue = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(numValue)) {
    return String(value)
  }

  const formattedValue = formatNumberNoDecimal(value)
  const className = textClassName ? `text-gray-700 block ${textClassName}` : 'text-gray-700 block text-sm'
  return (
    <span className={className} style={{ minWidth: 0, flexShrink: 1 }}>
      {formattedValue}
    </span>
  )
}

function formatCurrencyWithMonospace(
  value: string | number | null | undefined,
  symbol: string = '$',
  textClassName?: string,
  labelPosition?: 'above' | 'inline' | 'none'
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return labelPosition && labelPosition !== 'none' ? '-' : ''
  }

  const numValue = typeof value === 'number' ? value : parseFloat(String(value))
  if (isNaN(numValue)) {
    return String(value)
  }

  const formattedValue = formatCurrency(value, symbol, false)
  const className = textClassName ? `text-gray-700 block ${textClassName}` : 'text-gray-700 block text-sm'
  return (
    <span className={className} style={{ minWidth: 0, flexShrink: 1 }}>
      {formattedValue}
    </span>
  )
}

function getContactDisplayValueWithTags(
  value: any,
  mode: 'single' | 'multiple' = 'single',
  labelPosition?: 'above' | 'inline' | 'none',
  showValueTag: boolean = true,
  hyperlinked: boolean = true,
  hyperlinkModule: string = 'contacts',
  getRecordId?: (value: any, record?: any) => string | null,
  onHyperlinkClick?: (module: string, recordId: string, title: string) => void,
  readonly?: boolean,
  size?: 'small' | 'default',
  alignment?: 'left' | 'right' | 'center'
): React.ReactNode {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return labelPosition && labelPosition !== 'none' ? '-' : ''
  }

  // Transform value into contacts array format
  let contacts: Array<{ label: string; value?: any; id?: any; data?: any }> = []

  if (mode === 'single') {
    if (Array.isArray(value) && value.length > 0) {
      const contact = value[0]
      contacts = [
        {
          label: contact?.label || contact?.name || 'Selected Contact',
          value: contact?.value,
          id: contact?.id,
          data: contact?.data || contact
        }
      ]
    } else {
      contacts = [
        {
          label: value?.label || value?.name || String(value),
          value: value?.value,
          id: value?.id,
          data: value?.data || value
        }
      ]
    }
  } else {
    if (Array.isArray(value)) {
      contacts = value.map((contact) => ({
        label: contact?.label || contact?.name || 'Unknown',
        value: contact?.value,
        id: contact?.id,
        data: contact?.data || contact
      }))
    }
  }

  // If showValueTag is false, return plain text instead of badges
  if (!showValueTag) {
    if (hyperlinked && onHyperlinkClick) {
      // Render as clickable links when hyperlinked is true
      const links = contacts.map((contact, index) => {
        const contactId = getRecordId
          ? getRecordId(contact, null)
          : contact?.value || contact?.id || contact?.data?.id || null
        const contactName = contact.label || 'Unknown'

        if (!contactId) {
          return <React.Fragment key={index}>{contactName}</React.Fragment>
        }

        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation()
          e.preventDefault()

          // Prevent duplicate clicks within 500ms
          const clickKey = `${hyperlinkModule}-${contactId}`
          const now = Date.now()
          const lastClick = pendingHyperlinkClicksRef.current.get(clickKey) || 0

          if (now - lastClick < 500) {
            return // Ignore rapid duplicate clicks
          }

          pendingHyperlinkClicksRef.current.set(clickKey, now)
          onHyperlinkClick(hyperlinkModule, contactId, contactName)

          // Clean up after delay
          setTimeout(() => {
            pendingHyperlinkClicksRef.current.delete(clickKey)
          }, 500)
        }

        return (
          <React.Fragment key={contactId || index}>
            <span
              onClick={handleClick}
              className={`text-blue-600 hover:text-blue-800 hover:underline ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
            >
              {contactName}
            </span>
            {index < contacts.length - 1 && <span>, </span>}
          </React.Fragment>
        )
      })

      return <span className='text-gray-700'>{links}</span>
    }

    const names = contacts.map((c) => c.label).join(', ')
    return <span className='text-gray-700'>{names}</span>
  }

  // Handle hyperlink clicks
  const handleContactClick = (contact: any, contactId: string) => {
    if (onHyperlinkClick) {
      const contactName = contact?.label || contact?.name || 'Contact'
      onHyperlinkClick(hyperlinkModule, contactId, contactName)
    }
  }

  // Extract contact ID helper
  const getContactId = (contact: any): string | null => {
    if (getRecordId) {
      return getRecordId(contact, null) || null
    }
    return contact?.value || contact?.id || contact?.data?.id || null
  }

  const align = alignment === 'right' ? 'end' : 'start'

  return (
    <ContactBadges
      contacts={contacts}
      onContactClick={hyperlinked && onHyperlinkClick ? handleContactClick : undefined}
      getContactId={hyperlinked ? getContactId : undefined}
      size={size}
      align={align}
    />
  )
}

function getValuationDisplayValueWithTag(
  value: any,
  config: ValuationFieldConfig,
  textClassName?: string,
  labelPosition?: 'above' | 'inline' | 'none'
): React.ReactNode {
  if (!value) {
    return labelPosition && labelPosition !== 'none' ? '-' : ''
  }

  if (typeof value === 'object' && (value.preMoneyValue !== undefined || value.postMoneyValue !== undefined)) {
    const postMoney = value.postMoneyValue
    const preMoney = value.preMoneyValue

    const displayValue = postMoney && postMoney !== 0 ? postMoney : preMoney
    const displayType = postMoney && postMoney !== 0 ? 'Post' : 'Pre'

    if (displayValue && displayValue !== 0) {
      const currencySymbol = config.currencySymbol || '$'
      const formattedValue = formatNumberNoDecimal(displayValue)

      const spanClassName = textClassName ? `text-gray-700 ${textClassName}` : 'text-gray-700 text-sm'
      return (
        <div className='flex items-center justify-end gap-1 min-w-0'>
          <span className={spanClassName} style={{ minWidth: 0, flexShrink: 1 }}>
            {currencySymbol}
            {formattedValue}
          </span>
          {config.showValuationType && (
            <Tag style={{ width: 40, flexShrink: 0, textAlign: 'center' }} color={'blue'}>
              {displayType}
            </Tag>
          )}
        </div>
      )
    }
  }

  if (typeof value === 'number' && value !== 0) {
    const currencySymbol = config.currencySymbol || '$'
    const formattedValue = formatNumberNoDecimal(value)

    // If valuationType is provided in config, show tag
    if (config.showValuationType && config.valuationType) {
      const spanClassName = textClassName ? `text-gray-700 ${textClassName}` : 'text-gray-700 text-sm'
      return (
        <div className='flex items-center justify-end gap-1 min-w-0'>
          <span className={spanClassName} style={{ minWidth: 0, flexShrink: 1 }}>
            {currencySymbol}
            {formattedValue}
          </span>
          <Tag style={{ width: 40, flexShrink: 0, textAlign: 'center' }} color={'blue'}>
            {config.valuationType}
          </Tag>
        </div>
      )
    }

    return `${currencySymbol}${formattedValue}`
  }

  return labelPosition && labelPosition !== 'none' ? '-' : ''
}

export function getDisplayValue(config: FieldConfig, options: DisplayValueOptions = {}): string | React.ReactNode {
  const { textClassName, getLookupOptions, labelPosition, readonly, alignment } = options

  if (config.displayValue !== undefined) {
    return config.displayValue
  }

  const isEmpty = (val: any): boolean => {
    return val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0)
  }

  const emptyDisplay = labelPosition && labelPosition !== 'none' ? '-' : ''

  switch (config.type) {
    case FieldTypeEnum.NUMBER:
      return formatNumberWithMonospace(scalarForDisplay(config.value), textClassName, labelPosition)

    case FieldTypeEnum.CURRENCY:
      // A currency config is a NumberFieldConfig; the former fallback branch was unreachable.
      return formatCurrencyWithMonospace(
        scalarForDisplay(config.value),
        config.currencySymbol || '$',
        textClassName,
        labelPosition
      )

    case FieldTypeEnum.SELECT:
      if (isSelectFieldConfig(config)) {
        if (isEmpty(config.value)) {
          return emptyDisplay
        }

        // Render as tags if tagDisplay is enabled
        if (config.tagDisplay) {
          let tags: React.ReactNode[] = []

          if (config.selectType && config.selectType === SelectTypeEnum.LOOKUP && config.lookupCategory) {
            if (getLookupOptions) {
              const lookupOptions = getLookupOptions(config.lookupCategory)
              if (config.mode === 'multiple' && Array.isArray(config.value)) {
                tags = config.value
                  .map((val) => {
                    const option = lookupOptions.find((opt) => opt.value === val)
                    return option ? option.label : null
                  })
                  .filter((label): label is string => label !== null)
                  .map((label, index) => (
                    <Tag key={index} color='blue' className='mb-1'>
                      {label}
                    </Tag>
                  ))
              } else {
                const selectedOption = lookupOptions.find((option) => option.value === config.value)
                if (selectedOption) {
                  tags = [
                    <Tag key={0} color='blue'>
                      {selectedOption.label}
                    </Tag>
                  ]
                }
              }
            }
          } else if (config.options) {
            const values = Array.isArray(config.value) ? config.value : [config.value]
            tags = values
              .map((val) => {
                const option = config.options?.find((opt) => opt.value === val)
                return option ? option.label : null
              })
              .filter((label): label is string => label !== null)
              .map((label, index) => (
                <Tag key={index} color='blue' className='mb-1'>
                  {label}
                </Tag>
              ))
          }

          return tags.length > 0 ? <div className='flex flex-wrap gap-1'>{tags}</div> : emptyDisplay
        }

        // Default text display
        if (config.selectType && config.selectType === SelectTypeEnum.LOOKUP && config.lookupCategory) {
          if (getLookupOptions) {
            const lookupOptions = getLookupOptions(config.lookupCategory)
            // Handle arrays for multi-select lookups
            if (config.mode === 'multiple' && Array.isArray(config.value)) {
              const labels = config.value
                .map((val) => {
                  const option = lookupOptions.find((opt) => opt.value === val)
                  return option ? option.label : null
                })
                .filter((label): label is string => label !== null)
              return labels.length > 0 ? labels.join(', ') : emptyDisplay
            }
            const selectedOption = lookupOptions.find((option) => option.value === config.value)
            return selectedOption?.label || String(config.value || emptyDisplay)
          }
        } else if (config.options) {
          return getSelectDisplayValue(config.value, config.options, config.mode || 'single') || emptyDisplay
        }
      }
      return isEmpty(config.value) ? emptyDisplay : String(config.value)

    case FieldTypeEnum.TAGS:
      if (isTagsFieldConfig(config)) {
        if (isEmpty(config.value)) {
          return emptyDisplay
        }

        const tagsArray = Array.isArray(config.value) ? config.value : [config.value]
        const tags = tagsArray
          .filter((tag): tag is string => typeof tag === 'string' && tag.trim() !== '')
          .map((tag, index) => (
            <Tag key={index} color='blue' className='mb-1'>
              {tag}
            </Tag>
          ))

        return tags.length > 0 ? <div className='flex flex-wrap gap-1'>{tags}</div> : emptyDisplay
      }
      return emptyDisplay

    case FieldTypeEnum.COMPANY:
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      if (isCompanyFieldConfig(config)) {
        const mode = config.mode || 'single'
        const displayText = getCompanyDisplayValue(config.value, config.getCompanyDetailsById, mode) || emptyDisplay

        // If hyperlinked, render as clickable links using openFormInTab
        const isHyperlinked = config.hyperlinked !== undefined ? config.hyperlinked : true // Default to true
        const hyperlinkModule = config.hyperlinkModule || 'companies'

        // Handle founder_of field with badges (always show badges, clickable if hyperlinked)
        if (
          mode === 'multiple' &&
          Array.isArray(config.value) &&
          config.value.length > 0 &&
          config.key === 'prog__founder_of'
        ) {
          const handleCompanyBadgeClick =
            isHyperlinked && options.onHyperlinkClick
              ? (company: any, companyId: string) => {
                  const companyName = company?.label || company?.name || 'Unknown'
                  // Prevent duplicate clicks within 500ms
                  const clickKey = `${hyperlinkModule}-${companyId}`
                  const now = Date.now()
                  const lastClick = pendingHyperlinkClicksRef.current.get(clickKey) || 0

                  if (now - lastClick < 500) {
                    return // Ignore rapid duplicate clicks
                  }

                  pendingHyperlinkClicksRef.current.set(clickKey, now)
                  options.onHyperlinkClick!(hyperlinkModule, companyId, companyName)

                  // Clean up after delay
                  setTimeout(() => {
                    pendingHyperlinkClicksRef.current.delete(clickKey)
                  }, 500)
                }
              : undefined

          const getCompanyId = (company: any) => {
            return config.getRecordId
              ? config.getRecordId(company, null)
              : company?.value || company?.id || (typeof company === 'string' ? company : null)
          }

          return (
            <CompanyBadges
              companies={config.value}
              onCompanyClick={handleCompanyBadgeClick}
              getCompanyId={getCompanyId}
            />
          )
        }

        if (isHyperlinked && options.onHyperlinkClick) {
          const handleCompanyClick = (companyId: string, companyName: string, e: React.MouseEvent) => {
            e.stopPropagation()
            e.preventDefault()

            // Prevent duplicate clicks within 500ms
            const clickKey = `${hyperlinkModule}-${companyId}`
            const now = Date.now()
            const lastClick = pendingHyperlinkClicksRef.current.get(clickKey) || 0

            if (now - lastClick < 500) {
              return // Ignore rapid duplicate clicks
            }

            pendingHyperlinkClicksRef.current.set(clickKey, now)
            options.onHyperlinkClick!(hyperlinkModule, companyId, companyName)

            // Clean up after delay
            setTimeout(() => {
              pendingHyperlinkClicksRef.current.delete(clickKey)
            }, 500)
          }

          if (mode === 'multiple' && isListFieldValue(config.value) && config.value.length > 0) {
            const companyValues = config.value
            // Default: comma-separated links for other company fields
            const links = companyValues.map((company: any, index: number) => {
              const companyId = config.getRecordId
                ? config.getRecordId(company, null)
                : company?.value || company?.id || (typeof company === 'string' ? company : null)
              const companyName = company?.label || company?.name || (typeof company === 'string' ? company : 'Unknown')

              if (!companyId) {
                return <React.Fragment key={index}>{companyName}</React.Fragment>
              }

              return (
                <React.Fragment key={companyId || index}>
                  <span
                    onClick={(e) => handleCompanyClick(companyId, companyName, e)}
                    className={`text-blue-600 hover:text-blue-800 hover:underline ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    {companyName}
                  </span>
                  {index < companyValues.length - 1 && <span>, </span>}
                </React.Fragment>
              )
            })
            return <>{links}</>
          } else {
            // Single mode
            const company = Array.isArray(config.value) ? config.value[0] : config.value
            const companyId = config.getRecordId
              ? config.getRecordId(company, null)
              : company?.value || company?.id || (typeof company === 'string' ? company : null)
            const companyName = company?.label || company?.name || displayText

            if (!companyId) {
              return displayText
            }

            return (
              <span
                onClick={(e) => handleCompanyClick(companyId, companyName, e)}
                className={`text-blue-600 hover:text-blue-800 hover:underline ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {displayText}
              </span>
            )
          }
        }

        return displayText
      }
      // isCompanyFieldConfig is `type === COMPANY`, so nothing reaches past it in this case.
      return emptyDisplay

    case FieldTypeEnum.COUNTRY:
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      return getCountryDisplayValue(config.value, options.countriesData) || emptyDisplay

    case FieldTypeEnum.CITY:
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      return getCityDisplayValue(config.value) || emptyDisplay

    case FieldTypeEnum.CONTACT:
      if (isContactFieldConfig(config)) {
        const isHyperlinked = config.hyperlinked !== undefined ? config.hyperlinked : true // Default to true
        const hyperlinkModule = config.hyperlinkModule || 'contacts'
        const contactSize = config.size === 'small' ? 'small' : 'default'
        return getContactDisplayValueWithTags(
          config.value,
          config.mode || 'single',
          labelPosition,
          config.showValueTag !== undefined ? config.showValueTag : true,
          isHyperlinked,
          hyperlinkModule,
          config.getRecordId,
          options.onHyperlinkClick,
          readonly,
          contactSize,
          alignment
        )
      }
      // isContactFieldConfig is `type === CONTACT`, so nothing reaches past it in this case.
      return emptyDisplay

    case FieldTypeEnum.VALUATION:
      if (isValuationFieldConfig(config)) {
        return getValuationDisplayValueWithTag(config.value, config, textClassName, labelPosition)
      }
      return emptyDisplay

    // Contactable values render as a working link + an inline copy control
    // (owner request 2026-08-26). One case covers every form row AND grid
    // cell, since both read modes route through here. stopPropagation keeps
    // click-to-edit working — clicking beside the link still edits.
    case FieldTypeEnum.EMAIL:
    case FieldTypeEnum.PHONE:
    case FieldTypeEnum.URL: {
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      const contactValue = String(config.value)
      const contactHref =
        config.type === FieldTypeEnum.EMAIL
          ? mailHref(contactValue)
          : config.type === FieldTypeEnum.PHONE
            ? telHref(contactValue)
            : externalHref(contactValue)
      const external = config.type === FieldTypeEnum.URL
      return (
        <span className='inline-flex items-center gap-0.5 min-w-0 max-w-full'>
          <a
            href={contactHref}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className='text-blue-600 hover:text-blue-800 hover:underline truncate'
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {contactValue}
          </a>
          <CopyButton value={contactValue} iconOnly size='small' />
        </span>
      )
    }

    case FieldTypeEnum.TEXT:
    case FieldTypeEnum.TEXT_ACTION:
    case FieldTypeEnum.LINK:
    case FieldTypeEnum.TEXTAREA:
    case FieldTypeEnum.DATE:
    case FieldTypeEnum.TIME:
    case FieldTypeEnum.RICHTEXT:
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      const textValue = isDateFieldConfig(config)
        ? formatDateDisplay(config.value, config.format) || String(config.value)
        : defaultFormatters[config.type]?.(config.value) || String(config.value)

      // Handle hyperlinked text fields
      // LINK follows the same rules as TEXT_ACTION
      if (
        config.type === FieldTypeEnum.TEXT ||
        config.type === FieldTypeEnum.TEXT_ACTION ||
        config.type === FieldTypeEnum.LINK
      ) {
        const isHyperlinked = (config as any).hyperlinked === true
        const hyperlinkModule = (config as any).hyperlinkModule
        const getRecordId = (config as any).getRecordId
        const customHyperlinkClick = (config as any).onHyperlinkClick

        if (isHyperlinked && hyperlinkModule && getRecordId) {
          const recordId = getRecordId(config.value, null)
          if (recordId) {
            // Use custom handler if provided, otherwise use default
            const hyperlinkHandler = customHyperlinkClick || options.onHyperlinkClick
            if (hyperlinkHandler) {
              const handleTextClick = (e: React.MouseEvent) => {
                e.stopPropagation()
                e.preventDefault()

                // Prevent duplicate clicks within 500ms
                const clickKey = `${hyperlinkModule}-${recordId}`
                const now = Date.now()
                const lastClick = pendingHyperlinkClicksRef.current.get(clickKey) || 0

                if (now - lastClick < 500) {
                  return // Ignore rapid duplicate clicks
                }

                pendingHyperlinkClicksRef.current.set(clickKey, now)
                hyperlinkHandler(hyperlinkModule, recordId, String(textValue))

                // Clean up after delay
                setTimeout(() => {
                  pendingHyperlinkClicksRef.current.delete(clickKey)
                }, 500)
              }

              return (
                <span
                  onClick={handleTextClick}
                  className={`text-blue-600 hover:text-blue-800 hover:underline ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  {textValue}
                </span>
              )
            }
          }
        }
      }

      // Preserve newlines for textarea/richtext in read mode (HTML collapses whitespace by default)
      if (config.type === FieldTypeEnum.TEXTAREA || config.type === FieldTypeEnum.RICHTEXT) {
        return (
          <span style={{ whiteSpace: 'pre-wrap' }}>
            {textValue}
          </span>
        )
      }

      return textValue

    case FieldTypeEnum.SWITCH:
      return <span>{config.value ? 'Yes' : 'No'}</span>

    case FieldTypeEnum.CHECKBOX:
      return <span>{config.value ? 'Yes' : 'No'}</span>

    default:
      if (isEmpty(config.value)) {
        return emptyDisplay
      }
      return defaultFormatters[config.type]?.(config.value) || String(config.value || emptyDisplay)
  }
}
